/**
 * The brackets a strategy's Rental lengths offer (CU-869fkr1a9, brief
 * criteria 14-15, UX Obs. 7).
 *
 * The engine prices a bracket of a read pricelist from ONE rental length,
 * the bracket's reference LOR, and decides only in the LORs that fall
 * inside the strategy's Rental lengths (`compose.decided_lors_for` and
 * `matching.duration_covers` in the engine, criterion 24): a 30–59 bracket
 * with reference 59 is searched at 59 days only. The wizard's shortcuts
 * add the whole bracket ("8–9 · ref 9"); the engine still searches its
 * reference. Nothing is prefilled: an empty list means every bracket of
 * the product (brief Duda 2).
 *
 * Source, per product row: the row's own `tierConfig` when it has one,
 * otherwise the `tiers` of the pricelist the row reads. One bracket per
 * `(from, to)` even when two read pricelists share it; the open bracket and
 * any reference above the field's top (`DURATION_MAX_DAYS`) are clamped
 * to it.
 *
 * Pure module: no React, no fetch. Unit-tested with `node --test`.
 */

/** The upper bound of the wizard's duration field, in days: the last bracket of a ladder means "and longer". */
export const DURATION_MAX_DAYS = 180

const same = (a, b) => a != null && b != null && String(a) === String(b)
const int = (v) => (v === null || v === undefined || v === '' ? null : Number(v))

/** `7` (one day), `8–9 · ref 9`, `31+ · ref 31` (the open tail). */
export function bracketLabel(b) {
	if (b.open) return `${b.durationFrom}+ · ref ${b.referenceLor}`
	if (b.durationFrom === b.durationTo) return `${b.durationFrom}`
	return `${b.durationFrom}–${b.durationTo} · ref ${b.referenceLor}`
}

/**
 * `bracketsFromProduct(product, pricelists)` → `[{ durationFrom, durationTo,
 * referenceLor, open, label }]`, sorted, in the wizard's shape. No product,
 * no rows, no brackets → `[]`.
 */
export function bracketsFromProduct(product, pricelists, { max = DURATION_MAX_DAYS } = {}) {
	const seen = new Map()
	for (const row of product?.rows || []) {
		const own = Array.isArray(row.tierConfig) && row.tierConfig.length ? row.tierConfig : null
		const read = own ? null : (pricelists || []).find(p => same(p.id, row.readPricelistId))
		for (const t of own || read?.tiers || []) {
			const from = int(t.duration_from), to = int(t.duration_to), ref = int(t.reference_lor)
			if (!Number.isInteger(from) || from < 1 || !Number.isInteger(ref) || ref < 1) continue
			const open = to == null
			const durationFrom = Math.min(from, max)
			const durationTo = open ? max : Math.min(to, max)
			const key = `${durationFrom}-${durationTo}`
			if (seen.has(key)) continue
			const b = { durationFrom, durationTo, referenceLor: Math.min(ref, max), open }
			seen.set(key, { ...b, label: bracketLabel(b) })
		}
	}
	return [...seen.values()].sort((a, b) => a.durationFrom - b.durationFrom || a.durationTo - b.durationTo)
}

/** The distinct reference LORs of `brackets` that fall inside a range, sorted. */
export function referencesIn(range, brackets) {
	const from = Number(range?.durationFrom), to = Number(range?.durationTo)
	return [...new Set((brackets || []).map(b => b.referenceLor).filter(r => r >= from && r <= to))].sort((a, b) => a - b)
}

/**
 * True when no bracket's reference falls inside the range: the engine
 * neither searches nor decides anything for it (criterion 24). Without
 * brackets there is nothing to judge → false.
 */
export function rangeHasNoReference(range, brackets) {
	if (!(brackets || []).length) return false
	return referencesIn(range, brackets).length === 0
}
